/* eslint-disable max-lines-per-function, max-lines */
import get from "lodash.get";
import { EventBus, Events } from "jiffy-store";
import { api } from "jiffy-api";
import MadeStore from "components/jiffy/vue-product-details-print/store/store";
import { MODES } from "components/jiffy/vue-product-details-print/misc/constants";
import "./product-colors-slider.scss";
import { requestLocalStorage } from "common/utils/browser";
import updateRestockText from "components/jiffy/product-details-quantity-form/update-restock-text";
import showProductModule from "components/jiffy/product-details-quantity-form/show-product";
import {
  isFlashPdp,
  getFlashDeliveryText,
  getPageSourceParam
} from "components/jiffy/product-details/customizable-made-page-utils";

const SELECTORS = {
  colorPicker: "js-product-color-picker",
  item: "js-color-variant-selector",
  itemActive: "product-colors-slider__item-link--active",
  itemActiveJS: "js-color-variant-selector-active",
  modalContent: "#color-variant-selector-content",
  scrollToActive: "js-scroll-to-active-color",
  slider: "js-product-colors-slider",
  sliderArrow: "js-colors-slider-arrow",
  sliderControls: "js-colors-slider-controls",
  itemSameDayBadge: ".js-product-colors-slider-same-day",
  itemSameDayBadgeText: ".js-product-colors-slider-same-day-text"
};

const $getSameDayBadges = () => document.querySelectorAll(SELECTORS.itemSameDayBadge);
const $getItems = () => document.querySelectorAll(`.${SELECTORS.item}`);
const hideSameDayBadges = () => $getSameDayBadges().forEach($el => $el.classList.add("hidden"));

const getUrlParams = () => {
  const { searchParams } = new URL(window.location);
  const params = new URLSearchParams();
  ["page", "made"].forEach(name => {
    const param = searchParams.get(name);
    if (param !== null) {
      params.set(name, param);
    }
  });
  return params;
};

const getFlashCompositionData = () => {
  const { productId } = document.getElementById("product-page-container").dataset;
  return api.printOnDemand.getFlashCompositions(
    { slug: productId },
    { source: getPageSourceParam() }
  );
};

const deactivateItem = $context => {
  const $itemActive = $context.querySelector(`.${SELECTORS.itemActive}`);
  if ($itemActive) {
    $itemActive.classList.remove(SELECTORS.itemActive);
    $itemActive.classList.remove(SELECTORS.itemActiveJS);
  }
};

const scrollToActive = $colorsSlider => {
  const $colorPicker = $colorsSlider.querySelector(`.${SELECTORS.scrollToActive}`);
  if (!$colorPicker) return;

  const $activeItem = $colorPicker.querySelector(`.${SELECTORS.itemActive}`);
  if (!$activeItem) return;

  const shift = ($colorPicker.offsetWidth - $activeItem.offsetWidth) / 2;
  const targetLeft = $activeItem.offsetLeft - shift;

  $colorPicker.scroll({
    behavior: "auto",
    left: targetLeft
  });
};

const colorUpdateObserver = $context => {
  EventBus.on(Events.PRODUCT_COLOR_SELECTED, (evt, { color }) => {
    // Next event loop
    setTimeout(() => {
      // render new color name
      const $currentColor = document.querySelector(".js-current-color-name");
      if ($currentColor) $currentColor.innerHTML = color;

      // add active state to color trigger if color was changed from modal
      const $itemActive = $context.querySelector(`.${SELECTORS.itemActive}`);
      const currentColor = get($itemActive, "dataset.color");
      if (color !== currentColor) {
        const $nextActiveItem = $context.querySelector(`[data-color="${color}"]`);
        if ($nextActiveItem) {
          deactivateItem($context);
          $nextActiveItem.classList.add(SELECTORS.itemActive);
          $nextActiveItem.classList.add(SELECTORS.itemActiveJS);
          scrollToActive($context);
        }
      }
    });
  });
};

const initChangeColorHandler = $context => {
  $context.addEventListener("click", e => {
    e.preventDefault();
    const { target } = e;
    const $colorItem = target.classList.contains(SELECTORS.item)
      ? target
      : target.closest(`.${SELECTORS.item}`);

    const $variantContent = document.querySelector(SELECTORS.modalContent);

    if ($colorItem && $variantContent) {
      const { color } = $colorItem.dataset;
      const previousColorGroup = $($variantContent)
        .find("a.js-color-variant-selector.product-color-picker__item--active")
        .data("colorGroup");

      requestLocalStorage()?.setItem("PDPcolorPickerClicked", true);
      updateRestockText(color);

      // remove active state
      deactivateItem($context);
      // add active state
      $colorItem.classList.add(SELECTORS.itemActive);
      $colorItem.classList.add(SELECTORS.itemActiveJS);
      showProductModule.productVariantColorModal.selectColor(
        $colorItem.dataset || {},
        previousColorGroup
      );
      // publish event
      EventBus.publish(Events.PRODUCT_COLOR_SELECTED_FROM_SLIDER, this, { color });
      // update history path
      const params = getUrlParams();
      const path = `${$colorItem.pathname}${$colorItem.search}${
        params.size ? `&${params.toString()}` : ""
      }`;
      window.history.replaceState(null, null, path);
    }
  });
};

const handleArrowClick = ($colorPicker, $arrow) => {
  const { direction } = $arrow.dataset;
  const segmentWidth = Math.round($colorPicker.scrollWidth / $colorPicker.childElementCount);
  const currentItem = Math.round($colorPicker.scrollLeft / segmentWidth);
  let targetLeft;
  if (direction === "left") {
    targetLeft = (currentItem - 2) * segmentWidth;
  } else if (direction === "right") {
    targetLeft = (currentItem + 2) * segmentWidth;
  }

  targetLeft !== undefined &&
    $colorPicker.scroll({
      behavior: "smooth",
      left: targetLeft
    });
};

const handleSliderScroll = ($colorPicker, $arrows) => {
  const { offsetWidth, scrollLeft, scrollWidth } = $colorPicker;
  const [$arrowLeft, $arrowRight] = $arrows;
  const isArrowLeftDisabled = $arrowLeft.hasAttribute("disabled");
  const isArrowRightDisabled = $arrowRight.hasAttribute("disabled");

  // slider is at the leftmost position
  if (scrollLeft === 0) {
    !isArrowLeftDisabled && $arrowLeft.setAttribute("disabled", true);
  } else {
    isArrowLeftDisabled && $arrowLeft.removeAttribute("disabled");
  }

  // slider is at the rightmost position
  if (scrollWidth <= offsetWidth + scrollLeft) {
    !isArrowRightDisabled && $arrowRight.setAttribute("disabled", true);
  } else {
    isArrowRightDisabled && $arrowRight.removeAttribute("disabled");
  }
};

const willRenderSliderArrows = ($colorPicker, $colorsSlider) => {
  const { offsetWidth, scrollWidth } = $colorPicker;
  const $sliderControls = $colorsSlider.querySelectorAll(`.${SELECTORS.sliderControls}`);
  if ($sliderControls.length === 0) return;

  if (scrollWidth > offsetWidth) {
    Array.from($sliderControls).forEach($control => {
      $control.classList.remove("hidden");
    });
  }
};

const initSliderControls = $colorsSlider => {
  const $arrows = $colorsSlider.querySelectorAll(`.${SELECTORS.sliderArrow}`);
  if ($arrows.length === 0) return;

  const $colorPicker = $colorsSlider.querySelector(`.${SELECTORS.colorPicker}`);
  if (!$colorPicker) return;

  willRenderSliderArrows($colorPicker, $colorsSlider);

  Array.from($arrows).forEach($arrow => {
    $arrow.addEventListener("click", () => handleArrowClick($colorPicker, $arrow));
  });

  handleSliderScroll($colorPicker, $arrows);
  $colorPicker.addEventListener("scroll", () => handleSliderScroll($colorPicker, $arrows), {
    passive: true
  });
};

const initFlashComposition = async () => {
  const pageMode = MadeStore.getters["app/mode"];
  hideSameDayBadges();

  if (pageMode !== MODES.MADE) return;

  const {
    inventory,
    earliest_delivery: earliestDelivery,
    earliest_delivery_day: earliestDeliveryDay
  } = await getFlashCompositionData();
  const titleText = getFlashDeliveryText(earliestDelivery, earliestDeliveryDay);

  $getItems().forEach($el => {
    const { compositionId } = $el.dataset;
    if (inventory?.[compositionId]) {
      const $badge = $el.querySelector(SELECTORS.itemSameDayBadge);
      const $badgeText = $el.querySelector(SELECTORS.itemSameDayBadgeText);
      if ($badgeText) {
        $badgeText.innerText = titleText;
      }
      $badge?.classList?.remove("hidden");
      $badge?.classList.toggle("product-colors-slider__same-day--dedicated", isFlashPdp());
    }
  });
};

export default () => {
  const $colorsSlider = document.querySelector(`.${SELECTORS.slider}`);
  if (!$colorsSlider) return;

  initChangeColorHandler($colorsSlider);
  initSliderControls($colorsSlider);
  colorUpdateObserver($colorsSlider);
  scrollToActive($colorsSlider);
  initFlashComposition();
};
